import { Controller } from "@hotwired/stimulus";
import { computed, effect } from "@preact/signals-core";
import { initials, RING_COLORS, RING_INKS } from "../maps/tokens";
import type { LiveParticipant } from "../types/generated/live";
import type MapBoard from "./map_board_controller";

const PINGING_MS = 2500;
// Dots shown below Tailwind's sm, and from it; the rest become a +N chip. A phone's top bar has room for one dot and the chip.
const NARROW = 1;
const WIDE = 6;

// An account present before the snapshot names it is only its id.
type Participant = Pick<LiveParticipant, "id"> & Partial<Omit<LiveParticipant, "id">>;
export type Dot = { accountId: string; initials: string; label: string; background?: string; ink?: string; recolorable: boolean };
type Labels = { heading: string; inUse: string; failure: string };

// The master first, then by name without the handle's @; nameless last.
export function presenceDots(present: Iterable<string>, participants: Record<string, Participant>, selfId: string, selfLabel: string): Dot[] {
	const sortName = (participant: Participant) => participant.name?.replace(/^@/, "") ?? "￿";
	return [...present]
		.map((id) => participants[id] ?? { id })
		.sort((a, b) => Number(Boolean(b.master)) - Number(Boolean(a.master)) || sortName(a).localeCompare(sortName(b)))
		.map((participant) => {
			const name = participant.name || "?";
			return {
				accountId: participant.id,
				initials: initials(name.replace(/^@/, "")) || "?",
				label: participant.id === selfId ? selfLabel.replace("%{name}", () => name) : name,
				background: RING_COLORS[participant.color],
				ink: RING_INKS[participant.color],
				recolorable: Boolean(participant.participation_id),
			};
		});
}

// One swatch of an account's colour menu, as _color_picker marks
// it: checked when it is theirs, in use when anyone else holds it.
export function swatch(color: string, label: string, accountId: string, participants: Record<string, Participant>, inUseLabel: string) {
	const others = Object.values(participants).filter((other) => other.id !== accountId && other.color === color);
	const names = others
		.map((other) => other.name)
		.filter(Boolean)
		.join(", ");
	return {
		checked: participants[accountId]?.color === color,
		inUse: others.length > 0,
		title: names ? inUseLabel.replace("%{color}", () => label).replace("%{names}", () => names) : label,
	};
}

export function overflow(dots: Dot[], limit: number) {
	const rest = dots.slice(limit);
	return rest.length ? { text: `+${rest.length}`, label: rest.map((dot) => dot.label).join(", ") } : null;
}

// Connects to data-controller="map-presence", beside map-board on a board's
// root (never the table's): one dot per account with a board of the campaign
// open, in its colour, inside the status panel. On the master's board each dot
// is a button opening that member's colour menu (the picker targets).
export default class extends Controller {
	static targets = ["list", "template", "moreNarrow", "moreWide", "picker", "pickerTemplate"];
	static values = { selfLabel: String, labels: Object };
	declare readonly listTarget: HTMLElement;
	declare readonly templateTarget: HTMLTemplateElement;
	declare readonly moreNarrowTarget: HTMLElement;
	declare readonly moreWideTarget: HTMLElement;
	declare readonly pickerTarget: HTMLElement;
	declare readonly hasPickerTarget: boolean;
	declare readonly pickerTemplateTarget: HTMLTemplateElement;
	declare readonly selfLabelValue: string;
	declare readonly labelsValue: Labels;
	dispose?: () => void;
	pinging?: Map<string, ReturnType<typeof setTimeout>>;
	choosing?: string;

	// Turbo carrying the permanent root back reconnects this without a new ready.
	connect() {
		if (this.board?.renderer) this.ready();
	}

	disconnect() {
		this.closePicker(false);
		this.dispose?.();
		this.dispose = undefined;
		for (const timer of this.pinging?.values() ?? []) clearTimeout(timer);
		this.pinging?.clear();
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	ready() {
		const board = this.board;
		if (!board?.presence) return;
		this.dispose?.();
		const participants = computed(() => board.state.view.value.participants ?? {});
		this.dispose = effect(() => {
			this.render(presenceDots(board.presence.present.value, participants.value, board.accountIdValue, this.selfLabelValue));
		});
	}

	pinged({ detail: { accountId } }: { detail: { accountId: string } }) {
		this.pinging ??= new Map();
		clearTimeout(this.pinging.get(accountId));
		this.mark(accountId, true);
		this.pinging.set(
			accountId,
			setTimeout(() => {
				this.pinging.delete(accountId);
				this.mark(accountId, false);
			}, PINGING_MS),
		);
	}

	mark(accountId: string, on: boolean) {
		this.listTarget.querySelector(`[data-account-id="${accountId}"]`)?.toggleAttribute("data-pinging", on);
	}

	render(dots: Dot[]) {
		const focused = this.listTarget.contains(document.activeElement) && document.activeElement.closest<HTMLElement>("[data-account-id]")?.dataset.accountId;
		for (const old of this.listTarget.querySelectorAll("[data-account-id]")) old.remove();
		dots.slice(0, WIDE).forEach((dot, index) => {
			const item = this.templateTarget.content.firstElementChild.cloneNode(true) as HTMLElement;
			const face = item.querySelector("button") ?? item;
			item.dataset.accountId = dot.accountId;
			face.textContent = dot.initials;
			face.title = dot.label;
			face.setAttribute("aria-label", dot.label);
			if (face instanceof HTMLButtonElement) {
				face.disabled = !dot.recolorable;
				face.setAttribute("aria-expanded", String(this.choosing === dot.accountId));
			}
			if (dot.background) {
				item.style.backgroundColor = dot.background;
				item.style.color = dot.ink;
			}
			if (index >= NARROW) item.classList.add("max-sm:hidden");
			item.toggleAttribute("data-pinging", Boolean(this.pinging?.has(dot.accountId)));
			this.moreNarrowTarget.before(item);
			if (dot.accountId === focused) face.focus();
		});
		this.showMore(this.moreNarrowTarget, overflow(dots, NARROW));
		this.showMore(this.moreWideTarget, overflow(dots, WIDE));
		this.listTarget.hidden = false;
		if (this.choosing) this.fillPicker();
	}

	showMore(chip: HTMLElement, more: { text: string; label: string } | null) {
		chip.hidden = !more;
		chip.textContent = more?.text ?? "";
		chip.title = more?.label ?? "";
		if (more) chip.setAttribute("aria-label", `${more.text}: ${more.label}`);
	}

	face(accountId: string) {
		return this.listTarget.querySelector<HTMLElement>(`[data-account-id="${accountId}"] button`);
	}

	// A master's tap on a dot: its member's colour menu, or closing it again.
	pick(event: Event) {
		const accountId = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-account-id]")?.dataset.accountId;
		if (!this.hasPickerTarget || !accountId) return;
		if (this.choosing === accountId) return this.closePicker();
		this.openPicker(accountId);
	}

	openPicker(accountId: string) {
		const id = this.participants[accountId]?.participation_id;
		if (!id) return;
		this.closePicker(false);
		const menu = this.pickerTemplateTarget.content.cloneNode(true) as DocumentFragment;
		for (const form of menu.querySelectorAll("form")) form.action = form.getAttribute("action").replace(":id", id);
		for (const button of menu.querySelectorAll<HTMLElement>("[data-color]")) button.dataset.label = button.title;
		this.pickerTarget.replaceChildren(menu);
		this.choosing = accountId;
		this.pickerTarget.hidden = false;
		this.fillPicker();
		this.face(accountId)?.setAttribute("aria-expanded", "true");
		document.addEventListener("click", this.closeOnOutsideClick);
		document.addEventListener("keydown", this.closeOnEscape);
		this.pickerTarget.querySelector<HTMLElement>('[aria-checked="true"]')?.focus();
	}

	// Centred under the tapped dot, and kept inside the top bar.
	place(accountId: string) {
		const face = this.face(accountId);
		const bar = this.pickerTarget.offsetParent;
		if (!face || !bar) return;
		const dot = face.getBoundingClientRect();
		const box = bar.getBoundingClientRect();
		const width = this.pickerTarget.offsetWidth;
		this.pickerTarget.style.left = `${Math.max(0, Math.min(dot.left + dot.width / 2 - width / 2 - box.left, box.width - width))}px`;
	}

	// The participants are live, so an open menu follows every colour change.
	fillPicker() {
		const participants = this.participants;
		const participant = participants[this.choosing];
		if (!participant || !this.face(this.choosing)) return this.closePicker(false);
		this.pickerTarget.querySelector("[data-color-heading]").textContent = this.labelsValue.heading.replace("%{name}", () => participant.name || "?");
		for (const button of this.pickerTarget.querySelectorAll<HTMLElement>("[data-color]")) {
			const { checked, inUse, title } = swatch(button.dataset.color, button.dataset.label, this.choosing, participants, this.labelsValue.inUse);
			button.setAttribute("aria-checked", String(checked));
			button.setAttribute("aria-label", title);
			button.title = title;
			button.querySelector<HTMLElement>("[data-color-in-use]").hidden = !inUse;
		}
		this.place(this.choosing);
	}

	closePicker(refocus = true) {
		if (!this.choosing) return;
		const face = this.face(this.choosing);
		this.choosing = undefined;
		this.pickerTarget.hidden = true;
		this.pickerTarget.replaceChildren();
		face?.setAttribute("aria-expanded", "false");
		if (refocus) face?.focus();
		document.removeEventListener("click", this.closeOnOutsideClick);
		document.removeEventListener("keydown", this.closeOnEscape);
	}

	closeOnOutsideClick = (event: Event) => {
		const target = event.target as Element;
		if (!this.pickerTarget.contains(target) && !this.listTarget.contains(target)) this.closePicker(false);
	};

	// Stopped here, or the board's own Escape would also drop the selection.
	closeOnEscape = (event: KeyboardEvent) => {
		if (event.key !== "Escape" || !this.choosing) return;
		event.stopPropagation();
		this.closePicker();
	};

	// The same PATCH as the Jogadores menu, as JSON: the board never reloads,
	// and the participant upsert that follows repaints every board.
	async recolor(event: SubmitEvent) {
		event.preventDefault();
		const form = event.target as HTMLFormElement;
		const color = (event.submitter as HTMLElement | null)?.dataset.color;
		this.closePicker();
		let error: string | undefined;
		try {
			const response = await fetch(form.action, {
				method: "PATCH",
				headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": this.csrfToken },
				body: JSON.stringify({ participation: { color } }),
				credentials: "same-origin",
			});
			if (response.ok) return;
			error = (await response.json().catch(() => null))?.error;
		} catch {
			// Offline: the toast below says the colour stayed.
		}
		this.board?.toast(error || this.labelsValue.failure);
	}

	get participants(): Record<string, Participant> {
		return this.board?.state.view.peek().participants ?? {};
	}

	get csrfToken() {
		return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
	}
}
